// frontend/src/services/posQueue.ts
import api from "./api";

/* ------------------------------------------------------------------------ */
/* Types                                                                    */
/* ------------------------------------------------------------------------ */
export type QueueItem = {
  id: number;
  number: string;
  status: "draft" | "in_preparation" | "ready" | "delivered" | "paid" | string;
  created_at?: string;
  items: Array<{
    id?: number;
    name: string;
    qty: number;
    modifiers?: string[];
    status?: string;
    note?: string | null;
  }>;
  table?: string | number | null;
  ts?: string;
  note?: string | null;
};

/* ------------------------------------------------------------------------ */
/* Since-vremenske markere držimo lokalno (time-based delta)                */
/* ------------------------------------------------------------------------ */
let kitchenSince = 0;
let pickupSince = 0;

/* ------------------------------------------------------------------------ */
/* Helpers                                                                  */
/* ------------------------------------------------------------------------ */
const bearer = () => {
  const t = localStorage.getItem("device_token");
  if (!t) throw new Error("Nema tokena");
  return t;
};

function num(v: any, d = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
}

/** mapiraj rows iz BE /pos/open u FE QueueItem[] */
function normalizeOpenRows(rows: any[]): QueueItem[] {
  const arr = Array.isArray(rows) ? rows : [];
  return arr.map((o: any) => {
    const items = (o.items ?? []).map((i: any) => ({
      id: i.id,
      name: i.product_name ?? i.name ?? "Stavka",
      qty: num(i.quantity ?? i.qty ?? 1, 1),
      modifiers:
        (i.modifiers ?? [])
          .map((m: any) => m?.name)
          .filter((v: any) => typeof v === "string" && v.length > 0) ?? [],
      status: i.status,
      note: i.note ?? i.item_note ?? null,
    }));
    return {
      id: Number(o.id),
      number: String(o.number ?? o.id),
      status: String(o.status ?? ""),
      note: o.note ?? null,
      created_at: o.created_at,
      table: o.table ?? o.table_label ?? o.table_name ?? null,
      ts: o.ts,
      items,
    };
  });
}

/** tolerantni normalizer za fallback feed-ove */
function normalizeItem(raw: any): QueueItem {
  const id = raw.id ?? raw.order_id ?? raw.ID;
  const number =
    raw.number ?? raw.order_no ?? raw.display_no ?? raw.local_no ?? String(id);

  const srcItems = raw.items ?? raw.order_items ?? raw.lines ?? [];
  const items = (Array.isArray(srcItems) ? srcItems : []).map((it: any) => ({
    name: it.name ?? it.title ?? it.product_name ?? "Stavka",
    qty: it.qty ?? it.quantity ?? 1,
    modifiers:
      it.modifiers?.map((m: any) => m.name ?? m.title)?.filter(Boolean) ?? [],
  }));

  const status: QueueItem["status"] =
    (raw.status as any) ?? raw.state ?? raw.prep_status ?? "pending";

  const table = raw.table ?? raw.table_no ?? raw.table_name ?? null;
  const note = raw.note ?? null;

  return {
    id: Number(id),
    number: String(number),
    status,
    created_at: raw.created_at ?? raw.time ?? undefined,
    items,
    table,
    note,
  };
}

/* ------------------------------------------------------------------------ */
/* Kitchen: delta (time-based + long-polling)                               */
/* ------------------------------------------------------------------------ */
export async function fetchKitchenQueueDelta(
  wait = true, statuses: string[] = ["in_preparation"]
): Promise<{ rows: QueueItem[]; serverTime: number }> {
  const params: any = { since: kitchenSince || 0 };
  if (wait) params.wait = 1;
  statuses.forEach((status) => {
    params["statuses[]"] = status;
  });

  // po potrebi možeš dodati statuses[]=... ali BE već ima razuman default
  const { data } = await api.get(`/pos/open`, {
    params,
    headers: { Authorization: "Bearer " + bearer() },
  });

  const rows = normalizeOpenRows(data?.rows ?? data?.data ?? []);
  const serverTime = num(data?.serverTime ?? data?.meta?.server_time ?? Date.now() / 1000);

  // pomjeri since blago naprijed (−1s buffer protiv graničnog reza)
  kitchenSince = Math.max(0, Math.floor(serverTime) - 1);

  return { rows, serverTime };
}

export function resetKitchenSince() {
  kitchenSince = 0;
}

/* ------------------------------------------------------------------------ */
/* Pickup/Bar: delta (time-based + long-polling, filtriramo statuse)        */
/* ------------------------------------------------------------------------ */
export async function fetchPickupQueueDelta(
  wait = true, statuses: string[] = ["ready", "in_preparation", "sent", "open"]
): Promise<{ rows: QueueItem[]; serverTime: number }> {
  const params: any = {
    since: pickupSince || 0,
    // šank gleda ready + (po želji) in_preparation/sent/open
    "statuses[]": statuses,
  };
  if (wait) params.wait = 1;

  const { data } = await api.get(`/pos/open`, {
    params,
    paramsSerializer: (p) => {
      // ručno serijalizuj array statuses[] kao ponovljeni param
      const s = (p["statuses[]"] as string[]) || [];
      const base = [`since=${encodeURIComponent(p.since)}`];
      if (p.wait) base.push(`wait=1`);
      s.forEach((v) => base.push(`statuses[]=${encodeURIComponent(v)}`));
      return base.join("&");
    },
    headers: { Authorization: "Bearer " + bearer() },
  });

  const rows = normalizeOpenRows(data?.rows ?? data?.data ?? []);
  const serverTime = num(data?.serverTime ?? data?.meta?.server_time ?? Date.now() / 1000);

  pickupSince = Math.max(0, Math.floor(serverTime) - 1);

  return { rows, serverTime };
}

export function resetPickupSince() {
  pickupSince = 0;
}

/* ------------------------------------------------------------------------ */
/* Full list fallbacks (zadržano, ali prebacujemo prvo na /pos/open)        */
/* ------------------------------------------------------------------------ */
export async function fetchKitchenQueue(): Promise<QueueItem[]> {
  try {
    const { data } = await api.get(`/pos/open`, {
      headers: { Authorization: "Bearer " + bearer() },
    });
    return normalizeOpenRows(data?.rows ?? data?.data ?? []);
  } catch {
    // stariji feed
    const { data } = await api.get(`/pos/feed/kitchen`, {
      headers: { Authorization: "Bearer " + bearer() },
    });
    return (Array.isArray(data) ? data : data?.data || []).map(normalizeItem);
  }
}

export async function fetchPickupQueue(): Promise<QueueItem[]> {
  try {
    const { data } = await api.get(`/pos/open`, {
      params: { "statuses[]": ["ready", "in_preparation", "sent", "open"] },
      paramsSerializer: (p) => {
        const s = (p["statuses[]"] as string[]) || [];
        return s.map((v) => `statuses[]=${encodeURIComponent(v)}`).join("&");
      },
      headers: { Authorization: "Bearer " + bearer() },
    });
    return normalizeOpenRows(data?.rows ?? data?.data ?? []);
  } catch {
    // stariji feed
    const { data } = await api.get(`/pos/feed/pickup`, {
      headers: { Authorization: "Bearer " + bearer() },
    });
    return (Array.isArray(data) ? data : data?.data || []).map(normalizeItem);
  }
}

/* ------------------------------------------------------------------------ */
/* Status update                                                            */
/* ------------------------------------------------------------------------ */
export async function pushOrderStatus(
  orderId: number,
  status: "in_preparation" | "ready" | "delivered" | "paid" | "cancelled",
  fiscalNumber?: string,
) {
  await api.post(
    `/pos/orders/${orderId}/status`,
    { status, fiscalNumber },
    { headers: { Authorization: "Bearer " + bearer() } }
  );
}
